import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

export const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const plan = location.state?.plan || {
    name: 'Premium',
    price: '3.000.000',
    description: 'Solusi lengkap untuk kompleks besar'
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    complexName: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    navigate('/payment', { state: { plan, customer: formData } });
  };

  return (
    <div className="min-h-screen bg-brand-50 pt-24 pb-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-8 hover:bg-white/50"
        >
          <ArrowLeft className="mr-2" size={20} />
          Kembali ke Beranda
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Form Section */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-card rounded-3xl p-8 border border-border shadow-lg"
          >
            <h2 className="text-2xl font-bold text-foreground mb-6">Informasi Pelanggan</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-foreground/80 mb-2">Nama Lengkap</label>
                <Input
                  required
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="h-12"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground/80 mb-2">Email</label>
                <Input
                  required
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="h-12"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground/80 mb-2">No. Telepon</label>
                <Input
                  required
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="08123456789"
                  className="h-12"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground/80 mb-2">Nama Kompleks</label>
                <Input
                  required
                  name="complexName"
                  value={formData.complexName}
                  onChange={handleChange}
                  placeholder="Kompleks ABC"
                  className="h-12"
                />
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full h-14 text-lg font-semibold bg-brand-700 hover:bg-brand-800 text-white shadow-lg transform hover:-translate-y-0.5 transition-all"
                >
                  Lanjut ke Pembayaran
                </Button>
              </div>
            </form>
          </motion.div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="bg-brand-900 rounded-3xl p-8 text-white shadow-xl h-fit"
          >
            <h2 className="text-2xl font-bold mb-8">Ringkasan Pesanan</h2>

            <div className="space-y-6 mb-8">
              <div className="flex justify-between items-start pb-6 border-b border-white/10">
                <div>
                  <h3 className="text-xl font-semibold mb-1">Paket {plan.name}</h3>
                  <p className="text-white/60 text-sm">{plan.description}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">Rp {plan.price}</p>
                  <p className="text-white/60 text-sm">/ bulan</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between text-white/70">
                  <span>Subtotal</span>
                  <span>Rp {plan.price}</span>
                </div>
                <div className="flex justify-between text-white/70">
                  <span>Pajak (PPN 11%)</span>
                  <span>Rp {(parseInt(plan.price.replace(/\./g, '')) * 0.11).toLocaleString('id-ID')}</span>
                </div>
                <div className="flex justify-between items-center pt-4 border-t border-white/10">
                  <span className="font-semibold text-lg">Total</span>
                  <span className="font-bold text-3xl">
                    Rp {(parseInt(plan.price.replace(/\./g, '')) * 1.11).toLocaleString('id-ID')}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3 text-sm text-white/70 bg-white/5 p-4 rounded-xl">
                <ShieldCheck className="text-gold-400 shrink-0" size={24} />
                <p>Pembayaran Anda dijamin aman dan terenkripsi dengan standar keamanan tinggi.</p>
              </div>
              <div className="flex items-center gap-3 text-sm text-white/70 bg-white/5 p-4 rounded-xl">
                <CreditCard className="text-brand-300 shrink-0" size={24} />
                <p>Mendukung pembayaran via QRIS, Bank Transfer, dan E-Wallet.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
